import React, { useEffect } from 'react';
import {
  Box,
  Container,
  Heading,
  Text,
  Button,
  HStack,
  VStack,
  IconButton,
} from '@chakra-ui/react';
import { motion, AnimatePresence } from 'framer-motion';
import { FiCheck, FiDownload, FiSettings } from 'react-icons/fi';

import { useFileConversion } from './hooks/useFileConversion';
import Header from './components/layout/Header';
import Footer from './components/layout/Footer';
import FileDropzone from './components/core/FileDropzone';
import FeatureCards from './components/core/FeatureCards';
import CompassRose from './components/core/CompassRose';
import TopoBackdrop from './components/core/TopoBackdrop';
import LoadingAnimation from './components/animations/LoadingAnimation';
import SuccessAnimation from './components/animations/SuccessAnimation';
import { useConversionOptions } from './hooks/useConversionOptions';
import ConversionOptions from './components/core/ConversionOptions';

const accentRgb = 'var(--chakra-colors-brand-primary-rgb)';
const successRgb = 'var(--chakra-colors-brand-secondary-rgb)';

function App() {
  const {
    loading,
    isLoadingExiting,
    objectUrl,
    showDownload,
    showSuccessAnimation,
    outputFilename,
    selectedFile,
    handleFileDrop,
    handleFileRejected,
    convertFile
  } = useFileConversion();

  const { options, setOption, isOpen, toggleOpen } = useConversionOptions();

  useEffect(() => {
    return () => {
      if (objectUrl) {
        URL.revokeObjectURL(objectUrl);
      }
    };
  }, [objectUrl]);

  return (
    <Box minHeight="100vh" width="100%" position="relative" overflow="hidden">
      <TopoBackdrop />

      <Box position="relative" zIndex={1}>
        <Header />
      </Box>

      {/* Main Content */}
      <Container maxW="920px" py={{ base: 6, md: 10 }} position="relative" zIndex={1}>
        <VStack spacing={5} align="stretch">
          {/* Hero Section */}
          <VStack spacing={3} textAlign="center" mb={2} align="center">
            <Box
              display="inline-flex"
              alignItems="center"
              gap="8px"
              fontFamily="mono"
              fontSize="11.5px"
              letterSpacing="0.2em"
              textTransform="uppercase"
              color="brand.primary"
              px="12px"
              py="6px"
              borderRadius="999px"
              bg={`rgba(${accentRgb}, 0.12)`}
            >
              <Box
                as="span"
                w="6px"
                h="6px"
                borderRadius="50%"
                bg="brand.primary"
              />
              GPX · Keyhole · Free
            </Box>
            <Heading
              as="h1"
              fontWeight="extrabold"
              letterSpacing="-0.035em"
              lineHeight="1.02"
              sx={{
                fontSize: 'clamp(32px, 6vw, 64px)',
                background: `linear-gradient(95deg, var(--chakra-colors-text-primary) 0%, var(--chakra-colors-text-primary) 45%, var(--chakra-colors-brand-primary) 70%, var(--chakra-colors-brand-primary) 100%)`,
                WebkitBackgroundClip: 'text',
                backgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
              }}
            >
              Convert GPX to KML
            </Heading>
            <Text
              fontSize={{ base: 'md', md: 'lg' }}
              color="text.muted"
              maxW="520px"
            >
              Transform your GPS Exchange Format files to Keyhole Markup Language with just a few clicks. Free, private, and secure.
            </Text>
          </VStack>

          {/* Dropzone with floating compass */}
          <Box position="relative">
            <Box
              display={{ base: 'none', md: 'block' }}
              position="absolute"
              top="-28px"
              right="-28px"
              zIndex={2}
              pointerEvents="none"
            >
              <CompassRose size={80} />
            </Box>
            <FileDropzone
              selectedFile={selectedFile}
              onDrop={handleFileDrop}
              onDropRejected={handleFileRejected}
            />
          </Box>

          {/* Status (loading / success) */}
          <Box
            height="120px"
            position="relative"
            display="flex"
            alignItems="center"
            justifyContent="center"
          >
            <AnimatePresence mode="wait">
              {(loading || isLoadingExiting) && (
                <LoadingAnimation isExiting={isLoadingExiting} />
              )}
              {showSuccessAnimation && !loading && !isLoadingExiting && <SuccessAnimation />}
            </AnimatePresence>
          </Box>

          {/* CTA row */}
          <Box
            minHeight="48px"
            display="flex"
            justifyContent="center"
            alignItems="center"
          >
            <HStack spacing={4} justify="center">
              <AnimatePresence>
                {selectedFile && !loading && !isLoadingExiting && !showSuccessAnimation && (
                  <motion.div
                    key="convert"
                    initial={{ opacity: 0, scale: 0.8 }}
                    animate={{ opacity: 1, scale: 1 }}
                    exit={{ opacity: 0, scale: 0.8 }}
                    transition={{ duration: 0.2 }}
                  >
                    <Button
                      onClick={() => convertFile(options)}
                      leftIcon={<FiCheck />}
                      colorScheme="orange"
                      size="lg"
                      fontWeight="600"
                      px={8}
                      borderRadius="8px"
                      boxShadow={`0 2px 8px rgba(${accentRgb}, 0.30)`}
                      _hover={{ boxShadow: `0 4px 16px rgba(${accentRgb}, 0.40)`, transform: 'translateY(-2px)' }}
                      _active={{ boxShadow: `0 1px 4px rgba(${accentRgb}, 0.20)`, transform: 'translateY(0)' }}
                      transition="all 0.2s ease"
                      data-testid="convert-button"
                    >
                      Convert
                    </Button>
                  </motion.div>
                )}

                {selectedFile && !loading && !isLoadingExiting && !showSuccessAnimation && (
                  <motion.div
                    key="gear"
                    initial={{ opacity: 0, scale: 0.8 }}
                    animate={{ opacity: 1, scale: 1 }}
                    exit={{ opacity: 0, scale: 0.8 }}
                    transition={{ duration: 0.2 }}
                  >
                    <IconButton
                      aria-label="Styling options"
                      data-testid="styling-options-button"
                      icon={<FiSettings />}
                      onClick={toggleOpen}
                      variant="ghost"
                      size="lg"
                      colorScheme="orange"
                      isActive={isOpen}
                      borderRadius="8px"
                      sx={{
                        '&:hover, &[data-active]:hover': {
                          transform: 'translateY(-2px)',
                          boxShadow: `0 4px 16px rgba(${accentRgb}, 0.20)`,
                        },
                        '&:active': { transform: 'translateY(0)' },
                      }}
                    />
                  </motion.div>
                )}

                {showDownload && !loading && !isLoadingExiting && objectUrl && (
                  <motion.div
                    key="download"
                    initial={{ opacity: 0, scale: 0.8 }}
                    animate={{ opacity: 1, scale: 1 }}
                    exit={{ opacity: 0, scale: 0.8 }}
                    transition={{ duration: 0.2, delay: showSuccessAnimation ? 0.8 : 0 }}
                  >
                    <Button
                      as="a"
                      href={objectUrl}
                      download={outputFilename}
                      leftIcon={<FiDownload />}
                      colorScheme="green"
                      size="lg"
                      fontWeight="600"
                      px={8}
                      borderRadius="8px"
                      boxShadow={`0 2px 8px rgba(${successRgb}, 0.30)`}
                      _hover={{ boxShadow: `0 4px 16px rgba(${successRgb}, 0.40)` }}
                      _active={{ boxShadow: `0 1px 4px rgba(${successRgb}, 0.20)` }}
                      transition="all 0.2s ease"
                      data-testid="download-kml-button"
                    >
                      Download KML
                    </Button>
                  </motion.div>
                )}
              </AnimatePresence>
            </HStack>
          </Box>

          <AnimatePresence initial={false}>
            {isOpen && selectedFile && !loading && !isLoadingExiting && !showSuccessAnimation && (
              <motion.div
                key="options-panel"
                // marginBottom matches the parent VStack's `spacing={5}` (1.25rem) so the
                // trailing flex gap collapses in lockstep with the panel — without this the
                // gap disappears in one frame on unmount, snapping FeatureCards upward.
                // Using marginBottom (not marginTop) keeps the panel anchored to its top
                // edge so it shrinks in place instead of sliding up into the CTA row.
                initial={{ height: 0, opacity: 0, marginBottom: '-1.25rem' }}
                animate={{ height: 'auto', opacity: 1, marginBottom: 0 }}
                exit={{ height: 0, opacity: 0, marginBottom: '-1.25rem' }}
                transition={{ duration: 0.25, ease: 'easeInOut' }}
                style={{ overflow: 'hidden' }}
              >
                <ConversionOptions options={options} onChange={setOption} />
              </motion.div>
            )}
          </AnimatePresence>

          <FeatureCards />
        </VStack>
      </Container>

      <Box position="relative" zIndex={1}>
        <Footer />
      </Box>
    </Box>
  );
}

export default App;
